/**
 * Kiosk Menu Page - Browse and select items
 * 
 * Phase 1B: Rebuilt with new component architecture
 * Phase 1C: Added ItemDetailModal integration
 * - CategoryNav (left sidebar)
 * - MenuItemGrid (center content)
 * - KioskHeader (top, cart always visible)
 * - CartSummaryBar (bottom sticky)
 * - ItemDetailModal (full-screen for item customization)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import { useKiosk } from '../context/KioskContext';
import { toast } from 'sonner';

// Layout components
import KioskHeader from '../components/layout/KioskHeader';
import CartSummaryBar from '../components/layout/CartSummaryBar';

// Menu components
import CategoryNav from '../components/menu/CategoryNav';
import MenuItemGrid from '../components/menu/MenuItemGrid';

// Item detail modal
import { ItemDetailModal } from '../components/item-detail';

// Theme
import '../styles/kiosk-theme.css';

export default function MenuPage() {
  const navigate = useNavigate();
  const { 
    menu, 
    cart, 
    addToCart, 
    resetIdleTimer, 
    setView,
    loading: contextLoading 
  } = useKiosk();

  // Local state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [addingItemId, setAddingItemId] = useState(null);
  const [justAddedItemId, setJustAddedItemId] = useState(null);
  const [lastAddedItem, setLastAddedItem] = useState(null);
  
  // Item detail modal state
  const [showItemModal, setShowItemModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  // Initialize with first category when menu loads
  useEffect(() => {
    if (menu.categories?.length && !selectedCategory) {
      setSelectedCategory(menu.categories[0].id);
    }
    setView('menu');
  }, [menu.categories, selectedCategory, setView]);

  // Get current category items
  const currentCategory = useMemo(() => {
    return menu.categories?.find(c => c.id === selectedCategory);
  }, [menu.categories, selectedCategory]);

  const currentItems = useMemo(() => {
    if (!currentCategory?.items) return [];
    
    if (searchQuery.trim()) {
      return currentCategory.items.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return currentCategory.items;
  }, [currentCategory, searchQuery]);

  // Handle category selection
  const handleCategorySelect = useCallback((categoryId) => {
    setSelectedCategory(categoryId);
    setSearchQuery('');
    resetIdleTimer();
  }, [resetIdleTimer]);

  // Handle add to cart (for items without modifiers)
  const handleAddItem = useCallback((item) => {
    setAddingItemId(item.id);
    
    // Add to cart
    const cartItem = addToCart(item, [], 1, '');
    
    // Visual feedback
    setTimeout(() => {
      setAddingItemId(null);
      setJustAddedItemId(item.id);
      setLastAddedItem({ ...item, ...cartItem });
      
      toast.success(`Added ${item.name}`, {
        duration: 2000,
        icon: '🛒',
      });

      // Clear "just added" state after animation
      setTimeout(() => {
        setJustAddedItemId(null);
      }, 1500);
    }, 300);

    resetIdleTimer();
  }, [addToCart, resetIdleTimer]);

  // Handle opening item detail (for items with modifiers or when user taps card)
  const handleOpenItemDetail = useCallback((item) => {
    // Check if item has modifiers
    const hasModifiers = item.modifier_groups && item.modifier_groups.length > 0;
    
    if (hasModifiers) {
      // Open modal for customization
      setSelectedItem(item);
      setShowItemModal(true);
    } else {
      // No modifiers - quick add
      handleAddItem(item);
    }
    
    resetIdleTimer();
  }, [handleAddItem, resetIdleTimer]);

  // Handle modal close
  const handleCloseItemModal = useCallback(() => {
    setShowItemModal(false);
    setSelectedItem(null);
  }, []);

  // Handle add from modal
  const handleModalAddToCart = useCallback((data) => {
    // Show feedback
    setJustAddedItemId(data.item.id);
    setLastAddedItem({ ...data.item, totalPrice: data.totalPrice / data.quantity });
    
    // Clear "just added" state after animation
    setTimeout(() => {
      setJustAddedItemId(null);
    }, 1500);
  }, []);

  // Navigation handlers
  const handleBack = useCallback(() => {
    navigate('../start');
  }, [navigate]);

  const handleViewCart = useCallback(() => {
    setView('cart');
    navigate('../cart');
  }, [navigate, setView]);

  const handleSearchToggle = useCallback(() => {
    setShowSearch(prev => !prev);
    if (showSearch) {
      setSearchQuery('');
    }
  }, [showSearch]);

  // Touch/click handler for idle reset
  const handleInteraction = useCallback(() => {
    resetIdleTimer();
  }, [resetIdleTimer]);

  return (
    <div 
      className="kiosk-container min-h-screen flex flex-col"
      onClick={handleInteraction}
    >
      {/* Header */}
      <KioskHeader
        showBack={true}
        showSearch={true}
        showCart={true}
        subtitle={currentCategory?.name}
        onBackClick={handleBack}
        onSearchClick={handleSearchToggle}
        onCartClick={handleViewCart}
      />

      {/* Search Bar (expandable) */}
      {showSearch && (
        <div className="px-6 py-3 bg-[#1a1f26] border-b border-white/10 animate-slide-down">
          <div className="max-w-2xl mx-auto relative">
            <Search 
              size={20} 
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" 
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search menu items..."
              autoFocus
              className="
                w-full h-14 pl-12 pr-12
                bg-[#252b33] border-2 border-white/10
                rounded-xl
                text-white text-lg
                placeholder-gray-500
                focus:border-[#f5a623] focus:outline-none
                transition-colors
              "
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white p-1"
                aria-label="Clear search"
              >
                <X size={20} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Category Navigation (Left) */}
        <CategoryNav
          categories={menu.categories || []}
          activeCategory={selectedCategory}
          onCategorySelect={handleCategorySelect}
          className="hidden md:flex flex-shrink-0"
        />

        {/* Mobile Category Tabs */}
        <div className="md:hidden w-full overflow-x-auto bg-[#1a1f26] border-b border-white/10">
          <div className="flex p-2 gap-2 min-w-max">
            {menu.categories?.map((category) => (
              <button
                key={category.id}
                onClick={() => handleCategorySelect(category.id)}
                className={`
                  px-4 py-3 rounded-xl whitespace-nowrap
                  text-sm font-medium transition-all
                  min-h-[48px]
                  ${selectedCategory === category.id
                    ? 'bg-[#f5a623] text-[#0f1419]'
                    : 'bg-[#252b33] text-white/70 hover:bg-[#2d343d]'
                  }
                `}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        {/* Menu Item Grid (Center) */}
        <main className="flex-1 overflow-y-auto pb-32">
          <MenuItemGrid
            items={currentItems}
            loading={contextLoading}
            searchQuery={searchQuery}
            onAddItem={handleAddItem}
            onOpenItemDetail={handleOpenItemDetail}
            addingItemId={addingItemId}
            justAddedItemId={justAddedItemId}
          />
        </main>
      </div>

      {/* Cart Summary Bar (Bottom) */}
      <CartSummaryBar
        onViewCart={handleViewCart}
        lastAddedItem={lastAddedItem}
      />

      {/* Item Detail Modal */}
      {showItemModal && selectedItem && (
        <ItemDetailModal
          item={selectedItem}
          onClose={handleCloseItemModal}
          onAddToCart={handleModalAddToCart}
        />
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slide-down {
          from {
            opacity: 0;
            transform: translateY(-100%);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slide-down {
          animation: slide-down 0.2s ease-out;
        }
        
        /* Custom scrollbar for menu */
        main::-webkit-scrollbar {
          width: 8px;
        }
        main::-webkit-scrollbar-track {
          background: transparent;
        }
        main::-webkit-scrollbar-thumb {
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
        }
        main::-webkit-scrollbar-thumb:hover {
          background: rgba(255,255,255,0.2);
        }
      `}</style>
    </div>
  );
}
